import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SelectiveRestoreDialog = ({ open, onOpenChange, backup, onRestored }) => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [actionTypes, setActionTypes] = useState('');
  const [limit, setLimit] = useState('');
  const [busy, setBusy] = useState(false);

  if (!backup) return null;

  const submit = async () => {
    setBusy(true);
    const payload = { backup_id: backup.backup_id };
    if (fromDate) payload.from_date = new Date(fromDate).toISOString();
    if (toDate) payload.to_date = new Date(toDate + 'T23:59:59').toISOString();
    if (actionTypes.trim()) payload.action_types = actionTypes.split(',').map((s) => s.trim()).filter(Boolean);
    if (limit) payload.limit = parseInt(limit, 10);

    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/super-admin/db/audit-backups/restore`,
        payload,
        { withCredentials: true }
      );
      toast.success(
        `Restaurados ${data.restored} · saltados ${data.skipped} · ${data.matched_filter}/${data.total_in_backup} matcharam filtro`
      );
      onRestored?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro no restore seletivo');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="selective-restore-dialog">
        <DialogHeader>
          <DialogTitle>Restore seletivo</DialogTitle>
          <DialogDescription>
            Backup <code>{backup.backup_id}</code> · {backup.docs} docs em <em>{backup.scope}</em>.
            Todos os filtros são combinados (AND). Deixe vazio para restaurar tudo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Data inicial</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="restore-from-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Data final</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="restore-to-date" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Ações (separadas por vírgula)</Label>
            <Input
              placeholder="ex.: DPO_MEMBERSHIPS_SEEDED, TENANT_UPDATED"
              value={actionTypes}
              onChange={(e) => setActionTypes(e.target.value)}
              data-testid="restore-action-types"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Limite (nº máximo de docs)</Label>
            <Input
              type="number"
              min="1"
              placeholder="ex.: 100"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              data-testid="restore-limit"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="selective-restore-submit">
            {busy ? 'A restaurar...' : 'Restaurar com filtro'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SelectiveRestoreDialog;
